import {
  percentuaisVariaveis,
  type Cenario,
  type Parametros,
  type Recomendacao,
} from '@favo/core';

import { Camada, Celula } from '@/componentes/celula';
import { formatarMoeda, formatarPercentual, formatarPercentualPontos } from '@/design/formato';

import { AcoesDoQueFalta } from './acoes-do-que-falta';

/** O capítulo do que fazer. O julgamento é o mesmo do selo: sai de `rec.estado`. */
export function Recomendacoes({
  p,
  rec,
  atual,
}: {
  p: Parametros;
  rec: Recomendacao;
  atual: Cenario | null;
}) {
  return (
    <Celula como="section" className="secao">
      <h2 className="titulo-cartao titulo-secao">Recomendações</h2>
      <CorpoRecomendacoes p={p} rec={rec} atual={atual} />
    </Celula>
  );
}

function CorpoRecomendacoes({
  p,
  rec,
  atual,
}: {
  p: Parametros;
  rec: Recomendacao;
  atual: Cenario | null;
}) {
  const alvo = formatarPercentualPontos(p.lucroAlvo);

  if (rec.estado === 'abaixo') {
    const corteRelativo = rec.reducaoKit !== null && p.kit > 0 ? rec.reducaoKit / p.kit : null;
    return (
      <>
        <p className="prosa">
          O preço está abaixo do mínimo para a meta de <b>{alvo}</b> de lucro líquido. Há dois
          caminhos, e eles podem ser combinados.
        </p>

        <div className="favo opcoes">
          <Camada como="article" className="opcao opcao-preco">
            <span className="opcao-letra">Opção A</span>
            <h3 className="titulo-bloco">Reajustar o preço</h3>
            <p className="prosa">
              Levar o preço de <b>{formatarMoeda(p.ticket)}</b> para{' '}
              <b>{formatarMoeda(rec.precoSugerido)}</b> nas novas propostas, um acréscimo de{' '}
              <b>{formatarMoeda(rec.reajuste)}</b> por venda (
              {formatarPercentual(rec.reajustePct)}), mantendo o volume atual.
              {rec.sugerido.anual.lucro !== null
                ? ` Lucro anual projetado: ${formatarMoeda(rec.sugerido.anual.lucro)}.`
                : ''}
            </p>
          </Camada>

          {rec.reducaoKit !== null ? (
            <Camada como="article" className="opcao opcao-custo">
              <span className="opcao-letra">Opção B</span>
              <h3 className="titulo-bloco">Negociar o custo do kit</h3>
              <p className="prosa">
                Manter o preço de venda e negociar <b>{formatarMoeda(rec.reducaoKit)}</b> a menos no
                kit
                {corteRelativo !== null
                  ? ` (${formatarPercentual(corteRelativo)} dos ${formatarMoeda(p.kit)} atuais)`
                  : ''}
                . A conta já desconta a parcela que os percentuais sobre o serviço absorvem de cada
                real negociado, porque baixar o kit aumenta a base de serviço.
              </p>
              {corteRelativo !== null && corteRelativo > 0.15 ? (
                <p className="apoio-nota">
                  Um corte dessa ordem raramente sai só de negociação. Combine as duas opções:
                  reajuste parcial de preço e corte parcial de custo.
                </p>
              ) : null}
            </Camada>
          ) : null}
        </div>

        <ListaDeAcoes
          titulo="Como implementar"
          itens={[
            'Aplique o reajuste em propostas novas, de forma gradual, não em contrato já fechado.',
            'Apresente o que justifica o valor (garantia, suporte, prazo) antes de falar do preço.',
            'Acompanhe a taxa de conversão nas primeiras semanas para medir o efeito real no volume.',
          ]}
        />
      </>
    );
  }

  if (rec.estado === 'no-ponto') {
    return (
      <>
        <p className="prosa">
          O preço está dentro da tolerância de <b>{formatarMoeda(rec.tolerancia)}</b> em torno do
          mínimo para a meta de <b>{alvo}</b>. Nenhum ajuste de preço é recomendado agora.
        </p>
        <ListaDeAcoes
          titulo="Como manter"
          itens={[
            'Revise os custos diretos a cada orçamento: aumento de custo sem repasse derruba a margem abaixo da meta.',
            'Refaça o diagnóstico quando mudar kit, comissão, tributo ou despesa fixa.',
          ]}
        />
      </>
    );
  }

  if (rec.estado === 'acima') {
    return (
      <>
        <p className="prosa">
          A margem líquida atual, de <b>{formatarPercentual(atual?.lucroPct ?? null)}</b>, supera a
          meta de <b>{alvo}</b>. A folga de <b>{formatarMoeda(rec.folga)}</b> por venda (
          {formatarPercentual(rec.folgaPct)}) é margem conquistada, e o documento não sugere
          devolvê-la.
        </p>
        <ListaDeAcoes
          titulo="Como usar a folga"
          itens={[
            'Use a folga caso a caso, em negociação decisiva, nunca como redução do preço de tabela.',
            'Considere reinvestir parte do excedente em captação e pós-venda para sustentar o volume.',
            'Reavalie a meta em Empresa: se a operação entrega mais do que ela pede, a meta pode estar defasada.',
          ]}
        />
      </>
    );
  }

  if (rec.estado === 'meta-inviavel') {
    const pcts = percentuaisVariaveis(p);
    return (
      <>
        <p className="prosa">
          Com os percentuais cadastrados a meta é matematicamente inviável: eles somam{' '}
          <b>{formatarPercentual(pcts.total + pcts.serv)}</b> e, com a meta de <b>{alvo}</b>,
          atingem 100% ou mais do preço. Nenhum preço de venda fecha essa conta.
        </p>
        <ListaDeAcoes
          titulo="O que rever"
          itens={[
            `Comissão (${formatarPercentualPontos(p.comissao)}) e tributo (${formatarPercentualPontos(p.tributo)}): confira alíquota e base, venda total ou serviço, em Despesas.`,
            p.despesasExtras.length > 0
              ? `Outras despesas variáveis e extras (${p.despesasExtras.map((e) => `${e.nome} ${formatarPercentualPontos(e.valor)}`).join(', ')}): elimine percentual duplicado ou superestimado.`
              : 'Outras despesas variáveis: elimine percentual duplicado ou superestimado.',
            `Meta de lucro (${alvo}): se os percentuais estiverem corretos, a meta precisa caber no que sobra deles.`,
          ]}
        />
      </>
    );
  }

  if (rec.estado === 'prejuizo') {
    return (
      <>
        <p className="prosa">
          O preço praticado está abaixo do ponto em que o projeto se paga. Antes de discutir meta,
          a correção é sair do prejuízo.
        </p>
        <div className="mt-5">
          <ListaDeAcoes
            titulo="Para o projeto parar de consumir caixa"
            itens={[
              `Suba o preço para ${formatarMoeda(rec.precoLucroZero)} e o projeto passa a se pagar.`,
              `Suba para ${formatarMoeda(rec.precoSugerido)} e ele entrega a meta.`,
              ...(rec.reducaoKit === null
                ? []
                : [
                    `Ou mantenha o preço e negocie ${formatarMoeda(rec.reducaoKit)} a menos no kit com a distribuidora.`,
                  ]),
            ]}
          />
        </div>
      </>
    );
  }

  if (rec.estado === 'percentual-invalido') {
    return (
      <>
        <p className="prosa">
          Não há recomendação a emitir enquanto houver percentual fora da faixa de 0% a 100%.
          Corrija{' '}
          {rec.invalidos.map((i) => i.campo).join(', ')} e emita o laudo de novo.
        </p>
        <div className="mt-5">
          <ListaDeAcoes
            titulo="Para destravar o diagnóstico"
            // Roteia pela CHAVE, nunca pelo rótulo (mesma regra de
            // preco/veredito.tsx): comparar com o texto de exibição amarra a
            // navegação à redação e quebra em silêncio quando o rótulo muda.
            // A meta é editada na etapa Empresa; o resto, em Despesas.
            itens={rec.invalidos.map((i) =>
              i.chave === 'lucroAlvo'
                ? `Ajuste ${i.campo} em Empresa para um valor entre 0% e 100%.`
                : `Ajuste ${i.campo} em Despesas para um valor entre 0% e 100%.`,
            )}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <p className="prosa">
        {rec.falta === 'volume'
          ? 'Falta o volume anual de vendas. Sem ele o motor não rateia a despesa fixa e não há diagnóstico a emitir.'
          : 'Falta o valor de venda do projeto. Sem preço praticado não há diagnóstico a emitir.'}
      </p>
      <div className="mt-5">
        <AcoesDoQueFalta falta={rec.falta} />
      </div>
    </>
  );
}

function ListaDeAcoes({ titulo, itens }: { titulo: string; itens: string[] }) {
  return (
    <Camada className="mt-5 grid gap-2">
      <h3 className="titulo-bloco">{titulo}</h3>
      <ul className="lista">
        {itens.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Camada>
  );
}
